import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import Tooltip from "@mui/material/Tooltip";
import { useMemo } from "react";
import Txt from "../typography/Txt";
import Flx from "../layout/Flx";
import { useSelector } from "react-redux";
import { isArray, isEmpty, isNil } from "lodash";

const stringToColor = (str = "") => {
  const colors = [
    "#4f46e5",
    "#0891b2",
    "#059669",
    "#d97706",
    "#dc2626",
    "#7c3aed",
    "#2563eb",
    "#c026d3",
    "#0d9488",
    "#ea580c",
  ];
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const getInitials = (fullName, email) => {
  if (fullName && fullName !== email) {
    const parts = fullName.trim().split(/\s+/);
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return parts[0][0].toUpperCase();
  }
  if (email) return email[0].toUpperCase();
  return "?";
};

const UserAvatar = ({ label, tooltip, showTooltip, size = 24, sx }) => {
  const initials = getInitials(label, label);
  const bgColor = stringToColor(label);
  const fontSize = Math.max(size * 0.38, 10);

  const avatar = (
    <Avatar
      sx={{
        width: size,
        height: size,
        fontSize,
        fontWeight: 600,
        bgcolor: bgColor,
        color: "#fff",
        ...sx,
      }}
    >
      {initials}
    </Avatar>
  );

  if (showTooltip && tooltip) {
    return (
      <Tooltip title={tooltip} arrow placement="top">
        {avatar}
      </Tooltip>
    );
  }

  return avatar;
};

const UserEmailAvatarRow = ({
  sx,
  label,
  emptyLabel = "No users found",
  labelSx,
  size = 24,
  userEmails,
  includeName = false,
}) => {
  const users = useSelector((state) => state.users);

  const labelStyles = useMemo(() => {
    return {
      color: "#33475b",
      fontSize: "13px",
      ...labelSx,
    };
  }, [labelSx]);

  const avatarItems = useMemo(() => {
    if (!userEmails?.length || !isArray(userEmails)) return [];

    return userEmails
      .map((email) => {
        const user = users?.find((u) => u?.emailAddress === email);

        if (!user) {
          return {
            tooltip: email,
            fullName: email,
            emailAddress: email,
          };
        }

        return {
          tooltip: user.fullName
            ? `${user.fullName} <${user.emailAddress}>`
            : user.emailAddress,
          fullName: user.fullName || user.emailAddress,
          emailAddress: user.emailAddress,
        };
      })
      .sort((a, b) => a.fullName.localeCompare(b.fullName));
  }, [userEmails, users]);

  return (
    <Flx column sx={sx} className="avatar-stack-with-action" gap={0}>
      {label && <Txt sx={labelStyles}>{label}</Txt>}
      {isEmpty(avatarItems) || isNil(avatarItems) ? (
        <Txt secondary>{emptyLabel}</Txt>
      ) : (
        <Flx wrap g={includeName ? 1 : 0}>
          {avatarItems.map((item, i) => {
            if (includeName) {
              return (
                <Box
                  key={item?.emailAddress}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.6,
                    mr: 1,
                  }}
                >
                  <UserAvatar
                    size={size}
                    key={item?.emailAddress}
                    label={item?.fullName}
                    tooltip={item?.tooltip}
                    showTooltip
                    sx={{
                      border: "1.5px solid #ffffff",
                      // marginLeft: i === 0 ? 0 : -0.7,
                    }}
                  />
                  <Txt secondary>{item?.fullName}</Txt>
                </Box>
              );
            }
            return (
              <UserAvatar
                size={size}
                key={item?.emailAddress}
                label={item?.fullName}
                tooltip={item?.tooltip}
                showTooltip
                sx={{
                  border: "1.5px solid #ffffff",
                  marginLeft: i === 0 ? 0 : -0.7,
                }}
              />
            );
          })}
        </Flx>
      )}
    </Flx>
  );
};

export default UserEmailAvatarRow;
